import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Create a simple user context without authentication
const UserContext = createContext();

export const UserProvider = ({ children }) => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Initialize user state from localStorage if available
    useEffect(() => {
        const storedUser = localStorage.getItem('green_royal_hotel_user');
        if (storedUser) {
            try {
                const parsedUser = JSON.parse(storedUser);
                console.log('Restored user session from localStorage:', parsedUser.email);
                setUser(parsedUser);
            } catch (error) {
                console.error('Failed to parse stored user:', error);
                localStorage.removeItem('green_royal_hotel_user');
            }
        }
        setLoading(false);
    }, []);
    
    // Simple user management without authentication
    const setCurrentUser = (userData) => {
        console.log('UserContext: Setting current user data:', userData);
        if (userData) {
            setUser(userData);
            // Store user data in localStorage
            localStorage.setItem('green_royal_hotel_user', JSON.stringify(userData));
        } else {
            setUser(null);
            // Remove user data from localStorage
            localStorage.removeItem('green_royal_hotel_user');
        }
    };
    
    // Simple navigation helper
    const navigateTo = (path) => {
        navigate(path);
    };
    
    // Logout user
    const logoutUser = () => {
        setCurrentUser(null);
        localStorage.removeItem('green_royal_hotel_user');
        navigate('/landing');
    };

    // Login user
    const loginUser = (userData) => {
        console.log('Logging in user:', userData);
        setCurrentUser(userData);
        localStorage.setItem('green_royal_hotel_user', JSON.stringify(userData));
        navigate('/dashboard');
    };

    // Error during login
    const loginError = (error) => {
        setError(error);
        setCurrentUser(null);
        localStorage.removeItem('green_royal_hotel_user');
    };

    // Demo login (for development/testing)
    const demoLogin = (role = 'staff', department = 'Front Desk') => {
        const demoUser = {
            id: `demo-${role}-id`,
            email: `${role}@example.com`,
            role: role,
            department: department,
            fullName: `Demo ${role.charAt(0).toUpperCase() + role.slice(1)}`,
            createdAt: new Date().toISOString()
        };
        setCurrentUser(demoUser);
        localStorage.setItem('green_royal_hotel_user', JSON.stringify(demoUser));
        navigate('/dashboard');
    };

    // Demo logout
    const demoLogout = () => {
        setCurrentUser(null);
        localStorage.removeItem('green_royal_hotel_user');
        navigate('/landing');
    };

    return (
        <UserContext.Provider value={{ 
            user,
            currentUser: user, // Add currentUser alias for backward compatibility
            loading,
            setLoading,
            setCurrentUser,
            navigateTo,
            logoutUser,
            loginUser,
            loginError,
            demoLogin,
            demoLogout,
            error
        }}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
}; 